import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ReplaySubject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { enumHtsAssistantThreadItemKind } from '../../enums';
import { IHtsAssistantThreadItem } from '../../interfaces';

const SCROLL_DELAY_MS: number = 100;

/** Scrolling message list of the assistant drawer; keeps the newest item in view (#13383). */
@Component({
  selector: 'app-hts-assistant-thread',
  templateUrl: './hts-assistant-thread.component.html',
  styleUrls: ['./hts-assistant-thread.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HtsAssistantThreadComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() public items: IHtsAssistantThreadItem[] = [];
  /** The host shows the line's status chip with its reason: the carried notes would only repeat it (#13533). */
  @Input() public isHideCarried: boolean = false;
  @Input() public isReadonly: boolean = false;
  /** A turn / restart is in flight: result actions are held. */
  @Input() public isBusy: boolean = false;
  /** The drawer is locked (no saved description / panel being edited): results stay visible, actions are held. */
  @Input() public isLocked: boolean = false;
  /** Catalog mode (#13554): there is no line to insert into. */
  @Input() public isShowInsert: boolean = true;
  @Output() public insert: EventEmitter<number> = new EventEmitter<number>();
  @Output() public saveToCatalog: EventEmitter<number> = new EventEmitter<number>();
  @Output() public startOver: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('scrollingContainer', { static: true })
  public scrollingContainer: ElementRef<HTMLElement>;

  public kinds: typeof enumHtsAssistantThreadItemKind = enumHtsAssistantThreadItemKind;
  public visibleItems: IHtsAssistantThreadItem[] = [];

  private isDestroy: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.items || changes.isHideCarried) {
      const items: IHtsAssistantThreadItem[] = this.items || [];
      this.visibleItems = this.isHideCarried ? items.filter((item: IHtsAssistantThreadItem) => !item.isCarried) : items;
    }
    if (changes.items) {
      this.scrollToBottom();
    }
  }

  public ngAfterViewInit(): void {
    this.scrollToBottom();
  }

  public ngOnDestroy(): void {
    this.isDestroy.next(true);
    this.isDestroy.complete();
  }

  public get isActionsDisabled(): boolean {
    return this.isBusy || this.isLocked;
  }

  public trackByKey(index: number, item: IHtsAssistantThreadItem): string {
    return item.key;
  }

  private scrollToBottom(): void {
    timer(SCROLL_DELAY_MS)
      .pipe(takeUntil(this.isDestroy))
      .subscribe(() => {
        const element: HTMLElement = this.scrollingContainer ? this.scrollingContainer.nativeElement : null;
        if (!element || element.scrollHeight <= element.offsetHeight) {
          return;
        }
        element.scrollTo({ behavior: 'smooth', top: element.scrollHeight });
      });
  }
}
